import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { AdminDailyChart } from '@/components/admin/daily-chart';
import { cn } from '@/lib/utils';
import { adminArticleAnalyticsFn } from '@/server/functions/analytics';

const OPEN_MS = 520;

/** Reach panel shown on the public article page to signed-in staff only. */
export function ArticleStaffStats({ articleId }: { articleId: string }) {
    const contentId = useId();
    const [open, setOpen] = useState(false);
    const { data, isError, isPending } = useQuery({
        queryKey: ['article-staff-stats', articleId],
        queryFn: () => adminArticleAnalyticsFn({ data: { articleId } }),
        staleTime: 60_000,
    });

    useEffect(() => {
        if (!data) return;

        const reduceMotion =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduceMotion) {
            setOpen(true);
            return;
        }

        const frame = window.requestAnimationFrame(() => setOpen(true));
        return () => window.cancelAnimationFrame(frame);
    }, [data]);

    const statusLabel = isError
        ? 'לא ניתן לטעון את נתוני הצפיות'
        : isPending
          ? 'טוען נתוני צפיות…'
          : 'גלוי לצוות המערכת בלבד';

    return (
        <div className="px-6 py-4">
            <button
                type="button"
                className="flex w-full items-center justify-between gap-4 text-start transition-colors hover:text-white/85"
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen((value) => !value)}
            >
                <div className="min-w-0">
                    <h2 className="text-sm text-white/70">ביצועי הכתבה</h2>
                    <p className="mt-0.5 text-xs text-white/35">{statusLabel}</p>
                </div>
                <ChevronDown
                    aria-hidden
                    className={cn(
                        'size-4 shrink-0 text-white/35 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
                        open && 'rotate-180',
                    )}
                />
            </button>

            <div
                className={cn(
                    'grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
                    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
                style={{ transitionDuration: `${OPEN_MS}ms` }}
            >
                <div id={contentId} className="min-h-0 overflow-hidden">
                    <div
                        className={cn(
                            'pt-4 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:transform-none',
                            open
                                ? 'translate-y-0 opacity-100'
                                : '-translate-y-1 opacity-0',
                        )}
                        style={{ transitionDuration: `${OPEN_MS}ms` }}
                    >
                        {data ? (
                            <>
                                <dl className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-4">
                                    <Stat label="צפיות" value={data.totalViews} />
                                    <Stat
                                        label="מבקרים"
                                        value={data.totalVisitors}
                                    />
                                    <Stat
                                        label={`צפיות ב-${data.windowDays} ימים`}
                                        value={data.views7d}
                                    />
                                    <Stat
                                        label={`מבקרים ב-${data.windowDays} ימים`}
                                        value={data.visitors7d}
                                    />
                                </dl>

                                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-[1fr_16rem] sm:items-stretch">
                                    <AdminDailyChart
                                        className="min-w-0 order-2 border border-white/10 sm:order-1"
                                        fullWidth={false}
                                        title="צפיות לפי יום"
                                        subtitle={`${data.chartDays} הימים האחרונים`}
                                        primaryLabel="צפיות"
                                        data={data.viewsPerDay}
                                    />
                                    {data.referrers.length > 0 ? (
                                        <ReferrerBreakdown
                                            className="order-1 sm:order-2"
                                            rows={data.referrers}
                                            days={data.chartDays}
                                        />
                                    ) : null}
                                </div>

                                {data.countries.length > 0 && (
                                    <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/40">
                                        {data.countries.map((country) => (
                                            <li
                                                key={
                                                    country.code ??
                                                    country.label
                                                }
                                            >
                                                {country.label}
                                                <span
                                                    className="ms-1.5 tabular-nums text-white/25"
                                                    dir="ltr"
                                                >
                                                    {country.views.toLocaleString(
                                                        'he-IL',
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ReferrerBreakdown({
    rows,
    days,
    className,
}: {
    rows: {
        key: string;
        label: string;
        views: number;
        visitors: number;
    }[];
    days: number;
    className?: string;
}) {
    const maxViews = Math.max(1, ...rows.map((row) => row.views));

    return (
        <aside
            className={cn(
                'flex min-h-0 w-full shrink-0 flex-col border border-white/10 bg-black sm:h-full sm:overflow-hidden sm:w-auto',
                className,
            )}
        >
            <div className="shrink-0 border-b border-white/10 px-3 py-3 text-start">
                <h3 className="text-sm text-white/70">מקורות תנועה</h3>
                <p className="mt-0.5 text-[11px] text-white/35">
                    {days} הימים האחרונים
                </p>
            </div>
            <ul className="min-h-0 flex-1 divide-y divide-white/5 overflow-y-auto overscroll-contain">
                {rows.map((row) => (
                    <li key={row.key} className="px-3 py-2.5">
                        <div className="flex items-baseline justify-between gap-2">
                            <span
                                className="min-w-0 truncate text-xs text-white/70"
                                dir="auto"
                                title={row.label}
                            >
                                {row.label}
                            </span>
                            <span
                                className="shrink-0 tabular-nums text-[11px] text-white/40"
                                dir="ltr"
                            >
                                {row.views.toLocaleString('he-IL')}
                            </span>
                        </div>
                        <div className="mt-1.5 h-0.5 w-full bg-white/5">
                            <div
                                className="h-full bg-white/35"
                                style={{
                                    width: `${Math.max(
                                        4,
                                        (row.views / maxViews) * 100,
                                    )}%`,
                                }}
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </aside>
    );
}

function Stat({ label, value }: { label: string; value: number }) {
    return (
        <div className="bg-black px-4 py-3">
            <dt className="text-xs text-white/35">{label}</dt>
            <dd className="mt-1 text-xl text-white/80">
                <span className="tabular-nums" dir="ltr">
                    {value.toLocaleString('he-IL')}
                </span>
            </dd>
        </div>
    );
}
